import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

type BizAdminRoleGuardProps = {
  children: React.ReactNode;
};

// This guard component restricts access to bizadmin and owner roles only
export function BizAdminRoleGuard({ children }: BizAdminRoleGuardProps) {
  const { user, loading, userRole } = useAuth();
  const location = useLocation();
  const [checkingAccess, setCheckingAccess] = useState(true);
  
  useEffect(() => {
    // Once auth loading is complete, finish our access check
    if (!loading) {
      setCheckingAccess(false);
    }
  }, [loading]);

  // Debug logging
  useEffect(() => {
    console.log("BizAdminRoleGuard DEBUG:", {
      userRole,
      user_metadata: user?.user_metadata,
      loading,
      checkingAccess,
      path: location.pathname
    });
  }, [userRole, user, loading, checkingAccess, location.pathname]);

  // Show loading state while checking auth
  if (loading || checkingAccess) {
    console.log("BizAdminRoleGuard: Still loading auth state");
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if no user
  if (!user) {
    console.log("BizAdminRoleGuard: no user, redirecting to app domain auth");
    
    // Check if we're on marketing domain and need to redirect to app domain
    if (typeof window !== 'undefined' && !window.location.hostname.includes('app.')) {
      // On marketing domain - redirect to app domain for auth
      const appUrl = import.meta.env.DEV 
        ? (import.meta.env.VITE_SITE_URL || 'http://localhost:5173')
        : (import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com');
      window.location.href = appUrl + '/auth';
      return null;
    } else {
      // Already on app domain, use normal navigation
      return <Navigate to="/auth" state={{ from: location }} replace />;
    }
  }

  // Check if user has bizadmin or owner role
  const isBizAdmin = userRole === 'bizadmin' || userRole === 'owner';
  
  console.log("BizAdminRoleGuard: Access check result", { 
    userRole,
    isBizAdmin,
    user_email: user.email,
    access_granted: isBizAdmin
  });
  
  if (isBizAdmin) {
    console.log("BizAdminRoleGuard: access granted", { userRole });
    return <>{children}</>;
  }

  console.log("BizAdminRoleGuard: access denied", { userRole });
  return <Navigate to="/unauthorized" state={{ from: location }} replace />;
} 